/**
 * Asset endpoints — AIMS backend.
 * Source: /api/v1/aims/assets/*
 *
 * Endpoints defined here are scaffolded for future integration.
 * Pages currently still use the Redux mock store; switching to these
 * hooks is a follow-up task.
 */

import { aimsBackendApi } from "./api"
import {
  AnalysisType,
  // re-exported as a value so consumers can import enums directly from this file later
} from "./enums"
import {
  AnalysisTypeOption,
  AssetCreate,
  AssetCreatedResponse,
  AssetLocationValidationRequest,
  AssetLocationValidationResponse,
  AssetRead,
  ExcelUpload,
  PaginatedAssetList,
  PolygonAttachResponse,
  StagedRowsResponse,
  UploadPartialResponse,
  UploadValidResponse,
  VocabularyResponse,
} from "./types"

export type AssetListQuery = {
  analysis_type?: AnalysisType
  asset_type?: string
  asset_subcat?: string
  country?: string[]
  community?: string[]
  has_contracts?: boolean
  search?: string
  start_date_from?: string
  start_date_to?: string
  page?: number
  page_size?: number
}

export type VocabularyQuery = {
  asset_type?: string
}

export const aimsAssetsApi = aimsBackendApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * GET /api/v1/aims/assets/
     * Paginated list of assets, newest first.
     */
    listAssets: builder.query<PaginatedAssetList, AssetListQuery | void>({
      query: params => {
        const searchParams = new URLSearchParams()
        if (params?.page) searchParams.set("page", String(params.page))
        if (params?.page_size) searchParams.set("page_size", String(params.page_size))
        if (params?.analysis_type) searchParams.set("analysis_type", params.analysis_type)
        if (params?.asset_type) searchParams.set("asset_type", params.asset_type)
        if (params?.asset_subcat) searchParams.set("asset_subcat", params.asset_subcat)
        params?.country?.forEach(value => searchParams.append("country", value))
        params?.community?.forEach(value => searchParams.append("community", value))
        if (params?.has_contracts !== undefined) searchParams.set("has_contracts", String(params.has_contracts))
        if (params?.search) searchParams.set("search", params.search)
        if (params?.start_date_from) searchParams.set("start_date_from", params.start_date_from)
        if (params?.start_date_to) searchParams.set("start_date_to", params.start_date_to)

        return {
          url: "api/v1/aims/assets/",
          params: searchParams,
        }
      },
      providesTags: result => [
        { type: "AimsAssetList" as const, id: "LIST" },
        ...(result?.results ?? []).map(a => ({ type: "AimsAsset" as const, id: a.id })),
      ],
    }),

    /**
     * GET /api/v1/aims/assets/{id}/
     * Retrieve one asset by id.
     */
    getAsset: builder.query<AssetRead, number>({
      query: id => ({
        url: `api/v1/aims/assets/${id}/`,
      }),
      providesTags: (_r, _e, id) => [{ type: "AimsAsset" as const, id }],
    }),

    /**
     * POST /api/v1/aims/assets/
     * Create a single asset from manual input.
     * Returns 201 on success or 422 with field-level errors.
     */
    createAsset: builder.mutation<AssetCreatedResponse, AssetCreate>({
      query: body => ({
        url: "api/v1/aims/assets/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "AimsAssetList" as const, id: "LIST" }],
    }),

    /**
     * PATCH /api/v1/aims/assets/{id}/
     * Update an existing asset.
     */
    patchAsset: builder.mutation<AssetRead, { id: number; body: Partial<AssetCreate> }>({
      query: ({ id, body }) => ({
        url: `api/v1/aims/assets/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAsset" as const, id },
        { type: "AimsAssetList" as const, id: "LIST" },
      ],
    }),

    /**
     * POST /api/v1/aims/assets/upload/
     * Upload an Excel file with multiple assets (max 100 rows).
     * Returns 201 (all valid), 200 (partial), 422 (all invalid), or 400 (bad file).
     */
    uploadAssets: builder.mutation<UploadValidResponse | UploadPartialResponse, ExcelUpload>({
      query: ({ file, country, kmzZip }) => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("country", country)
        if (kmzZip) {
          formData.append("kmz_files", kmzZip)
        }
        return {
          url: "api/v1/aims/assets/upload/",
          method: "POST",
          body: formData,
        }
      },
      invalidatesTags: [{ type: "AimsAssetList" as const, id: "LIST" }],
    }),

    validateAssetLocation: builder.mutation<AssetLocationValidationResponse, AssetLocationValidationRequest>({
      query: body => ({
        url: "api/v1/aims/validate-location/",
        method: "POST",
        body,
      }),
    }),

    /**
     * GET /api/v1/aims/assets/staged/{batch_id}/
     * Retrieve invalid rows staged from a partial upload.
     */
    getStagedRows: builder.query<StagedRowsResponse, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `api/v1/aims/assets/staged/${batchId}/`,
      }),
      providesTags: (_r, _e, { batchId }) => [{ type: "AimsAssetStaged" as const, id: batchId }],
    }),

    /**
     * GET /api/v1/aims/assets/staged/{batch_id}/download/
     * Download an Excel correction file containing only invalid staged rows.
     */
    downloadStagedRows: builder.mutation<Blob, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `api/v1/aims/assets/staged/${batchId}/download/`,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    /**
     * DELETE /api/v1/aims/assets/staged/{batch_id}/delete/
     * Discard a batch of staged rows (e.g. before re-uploading).
     */
    deleteStagedRows: builder.mutation<void, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `api/v1/aims/assets/staged/${batchId}/delete/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { batchId }) => [{ type: "AimsAssetStaged" as const, id: batchId }],
    }),

    /**
     * GET /api/v1/aims/assets/template/
     * Download the empty Excel template (xlsx).
     * Returns a Blob for the file download.
     */
    downloadAssetsTemplate: builder.mutation<Blob, void>({
      query: () => ({
        url: "api/v1/aims/assets/template/",
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    /**
     * POST /api/v1/aims/assets/polygons/export/
     * Download available KMZ polygon files for selected assets as a zip.
     * Returns an empty Blob when no polygon files are available.
     */
    downloadAssetPolygons: builder.mutation<Blob, { asset_ids: number[] }>({
      query: body => ({
        url: "api/v1/aims/assets/polygons/export/",
        method: "POST",
        body,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    /**
     * GET /api/v1/aims/assets/{id}/polygon/download/
     * Download one asset KMZ polygon file.
     */
    downloadAssetPolygon: builder.mutation<Blob, number>({
      query: id => ({
        url: `api/v1/aims/assets/${id}/polygon/download/`,
        method: "GET",
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    /**
     * GET /api/v1/aims/assets/vocabulary/
     * Without params: returns asset types.
     * With ?asset_type=X: returns subcategories for that type.
     */
    getAssetVocabulary: builder.query<VocabularyResponse, VocabularyQuery | void>({
      query: params => ({
        url: "api/v1/aims/assets/vocabulary/",
        params: params || undefined,
      }),
      providesTags: [{ type: "AimsAssetVocabulary" as const, id: "LIST" }],
    }),

    /**
     * GET /api/v1/aims/assets/analysis-types/
     * Returns the analysis-type options the backend currently accepts.
     */
    getAnalysisTypes: builder.query<AnalysisTypeOption[], void>({
      query: () => ({
        url: "api/v1/aims/assets/analysis-types/",
      }),
      providesTags: [{ type: "AimsAssetVocabulary" as const, id: "ANALYSIS_TYPES" }],
    }),

    /**
     * DELETE /api/v1/aims/assets/{id}/
     * Delete a single asset. Cascades to AssetAnalysisRound and ContractAsset rows.
     */
    deleteAsset: builder.mutation<void, number>({
      query: id => ({
        url: `api/v1/aims/assets/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AimsAsset" as const, id },
        { type: "AimsAssetList" as const, id: "LIST" },
      ],
    }),

    /**
     * POST /api/v1/aims/assets/{id}/polygon/
     * Attach a single KMZ-derived polygon to an existing asset.
     * The KMZ is sent as multipart `kmz`. Validation is the same as the
     * bulk upload (AC1–AC3): rejection comes back as a 200 with
     * `status: "warning"` rather than an HTTP error.
     */
    attachAssetPolygon: builder.mutation<PolygonAttachResponse, { id: number; kmz: File }>({
      query: ({ id, kmz }) => {
        const formData = new FormData()
        formData.append("kmz", kmz)
        return {
          url: `api/v1/aims/assets/${id}/polygon/`,
          method: "POST",
          body: formData,
        }
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsAsset" as const, id },
        { type: "AimsAssetList" as const, id: "LIST" },
      ],
    }),
  }),
})

export const {
  useListAssetsQuery,
  useGetAssetQuery,
  useLazyGetAssetQuery,
  useCreateAssetMutation,
  usePatchAssetMutation,
  useUploadAssetsMutation,
  useValidateAssetLocationMutation,
  useGetStagedRowsQuery,
  useDownloadStagedRowsMutation,
  useDeleteStagedRowsMutation,
  useDownloadAssetsTemplateMutation,
  useDownloadAssetPolygonsMutation,
  useDownloadAssetPolygonMutation,
  useGetAssetVocabularyQuery,
  useGetAnalysisTypesQuery,
  useDeleteAssetMutation,
  useAttachAssetPolygonMutation,
} = aimsAssetsApi
